import { precosOrigemDestino } from "@/services/pagesWeb";
import { PrecoOrigemDestino } from "@/types/Endereco";
import { formatMoneyBr } from "@/utils/format";
import { regions } from "@/utils/locales";
import { ArrowRight } from "lucide-react";

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
interface TransportPriceSectionProps {
  cityName: string;
  stateName: string;
}

export const TransportPriceSection = ({
  cityName,
  stateName,
}: TransportPriceSectionProps) => {
  const [transportRoutes, setTransportRoutes] = useState<PrecoOrigemDestino[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPrecos = async () => {
      try {
        const cidadeAtual = regions
          .flatMap((region) => region.cities)
          .find(
            (city) =>
              city.name.toLowerCase() === cityName.toLowerCase() &&
              city.state.toLowerCase() === stateName.toLowerCase()
          );

        if (!cidadeAtual) {
          throw new Error("Cidade não encontrada");
        }

        const precos = await precosOrigemDestino(cidadeAtual.id);
        setTransportRoutes(precos.slice(0, 5)); // Pegando os 5 primeiros resultados
        setIsLoading(false);
      } catch (err) {
        setError("Erro ao carregar preços");
        setIsLoading(false);
        console.error("Erro ao buscar preços:", err);
      }
    };

    fetchPrecos();
  }, [cityName, stateName]);

  if (isLoading) {
    return (
      <section className=" py-10 md:py-24 ">
        <div className="text-center">Carregando preços...</div>
      </section>
    );
  }

  if (error) {
    return (
      <section className=" py-10 md:py-24 ">
        <div className="text-center text-n-error">{error}</div>
      </section>
    );
  }

  return (
    <section id="preco-transporte" className="py-10 md:py-24 bg-n-bg-green">
      <div className="container">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
          {/* Texto da esquerda */}
          <div className="">
            <h2
              className="text-3xl md:text-4xl lg:text-5xl text-n-secondary"
              style={{ fontFamily: "'Anton', sans-serif" }}
            >
              Quanto custa o transporte <br /> de veículos em {cityName}?
            </h2>

            <p className="mt-6 md:text-lg text-n-text-light">
              O valor do frete varia conforme a distância, tipo de veículo,
              modalidade escolhida e outros fatores. Para ajudar na sua decisão,
              mostramos abaixo alguns valores médios de rotas populares para
              veículos pequenos:
            </p>
          </div>

          {/* Tabela de preços */}
          <div className="w-full items-center justify-center flex flex-col">
            <div className="space-y-4">
              {transportRoutes.slice(0, 5).map((route, index) => (
                <div
                  key={index}
                  className="flex items-center lg:text-lg md:text-md sm:text-sm text-[10px] md:gap-2 gap-1 justify-start"
                >
                  <span className="text-n-beige md:rounded-full rounded-lg border border-n-beige py-1 px-3">
                    {(() => {
                      const [city, state] = route.origem.split("-");
                      return `${city.charAt(0).toUpperCase()}${city
                        .slice(1)
                        .toLowerCase()} - ${state.trim().toUpperCase()}`;
                    })()}
                  </span>
                  <ArrowRight className="md:h-6 md:w-6 h-3 w-3 text-n-beige" />
                  <span className="text-n-beige md:rounded-full rounded-lg border border-n-beige py-1 px-3">
                    {(() => {
                      const [city, state] = route.destino.split("-");
                      return `${city.charAt(0).toUpperCase()}${city
                        .slice(1)
                        .toLowerCase()} - ${state.trim().toUpperCase()}`;
                    })()}
                  </span>

                  <span className="font-semibold md:rounded-full rounded-lg py-1 px-2 text-n-green bg-n-green-s-light ml-2">
                    {formatMoneyBr(route.preco_medio)}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-center mt-10">
              <Link to="/fazer-cotacao">
                <Button id="btn-fazer-cotacao">
                  Agendar meu transporte agora <ArrowRight className="ml-2 " />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
